import { Link, useHistory } from 'react-router-dom'
import s from './index.module.css'
import { useState, useEffect, useMemo, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { selectProfile } from '@store/profile.reducer'
import RoleList from './components/RoleList/RoleList'
import { formateName } from '@helpers/formateName'
import { getAvatar } from '@helpers/getAvatar'
import {
  AuthService,
  idUserAuthorizedOnTheAuthorizationPage,
} from '@/services/AuthService'
import { useScreenWidth } from '@hooks/useScreenWidth'
import { Drawer, Dropdown, Tooltip } from 'antd'
import { NavigationApp } from '@common/Sidebar'
import classNames from 'classnames'

import QModals from '@/components/pages/Main/Requests/Qmodals/index.jsx'
import { ButtonEditNew } from '@UI/Button'
import {
  CloseIcon,
  LogOutIcon,
  SupportIcon,
  Feedback,
  RatingIcon,
} from '@/utils/icons'
import BalanceBlock from '@UI/BalanceBlock'

import Notification from './components/notifications/index.jsx'
import Further from './components/Further'
import { getMyLevel } from '@/helpers/getMyLevel'

import { openModal } from '@store/modalsSlise'
import roleActive from '@/helpers/roleActive'
import LicenseAndPaymentButton from '../Sidebar/components/LicenseAndPaymentButton/LicenseAndPaymentButton'
import { images, settingApi, timetableBeta } from '@/api'
import { useTelegramBotIntegration } from './hooks/useTelegramBotIntegration'
import { useTelegramNotificationBotIntegration } from './hooks/useTelegramNotificationBotIntegration'
import { useVkNotificationCommunityIntegration } from './hooks/useVkNotificationCommunityIntegration'
import { useMaxNotificationBotIntegration } from './hooks/useMaxNotificationBotIntegration'
import { useMaxBotIntegration } from './hooks/useMaxBotIntegration'
import ActivateTelegramBot from '@/components/modals/ActivateTelegramBot'
import ActivateTelegramNotificationBot from '@/components/modals/ActivateTelegramNotificationBot'
import DeactivateTelegramNotificationBot from '@/components/modals/DeactivateTelegramNotificationBot'
import ActivateVkNotificationCommunity from '@/components/modals/ActivateVkNotificationCommunity'
import DeactivateVkNotificationCommunity from '@/components/modals/DeactivateVkNotificationCommunity'
import ActivateMaxNotificationBot from '@/components/modals/ActivateMaxNotificationBot'
import DeactivateMaxNotificationBot from '@/components/modals/DeactivateMaxNotificationBot'
import ActivateMaxBot from '@/components/modals/ActivateMaxBot'
import { getNotificationIntegrationSettingsUrl } from '@/helpers/notificationIntegration'
import dayjs from 'dayjs'
import { typesEnum } from '@pages/Main/Timetable/components/Types/typesEnum'
import CustomGoogleTranslate from '@common/Header/components/CustomGoogleTranslate'
import ThemeSwitch from '@common/Header/components/ThemeSwitch/ThemeSwitch'
import { isIOSDevice } from '@/helpers/new/isIOSDevice'
import TYPES from '@modals/TYPES'
import { useAccountActions } from '@/hooks/useAccountActions'
import { useIsCurrentPage } from '@/hooks/useIsCurrentPage'
import { SETTINGS_TYPES } from '@/constant'
import { actionSetItemSetting } from '@store/settings-reducer'

const isEnabledSetting = (value) => value === '1' || value === 1 || value === true

const normalizeSearchValue = (value) => String(value ?? '').toLowerCase().trim()

const lowVisionSpeechStorageKey = 'lowVisionSpeechModeEnabled'

const getSearchTokens = (value) =>
  normalizeSearchValue(value)
    .split(/[^a-zа-яё0-9]+/i)
    .filter(Boolean)

const getMenuItemSearchAliases = (item) => {
  const value = normalizeSearchValue(`${item.title} ${item.url}`)
  const aliases = []

  if (value.includes('dz') || value.includes('home-work')) {
    aliases.push('дз', 'домашка', 'домашнее задание', 'домашние задания')
  }

  if (value.includes('control')) {
    aliases.push('кр', 'контрольная', 'контрольные', 'контрольная работа')
  }

  if (value.includes('probe')) {
    aliases.push('пр', 'пробник', 'проверочная')
  }

  return aliases.join(' ')
}

const findOnCurrentPage = (value, back = false) => {
  const search = value.trim()
  if (!search || typeof window.find !== 'function') return

  const isFound = window.find(search, false, back, true, false, false, false)

  if (!isFound) {
    getSearchTokens(search).some((token) =>
      window.find(token, false, back, true, false, false, false),
    )
  }
}

const getElementTextForSpeech = (element) => {
  const text =
    element?.getAttribute?.('aria-label') ||
    element?.getAttribute?.('title') ||
    element?.getAttribute?.('alt') ||
    element?.innerText ||
    element?.textContent ||
    element?.value ||
    element?.placeholder ||
    ''

  return String(text).replace(/\s+/g, ' ').trim().slice(0, 160)
}

const getElementSpeechPrefix = (element) => {
  const tagName = element?.tagName?.toLowerCase()
  const role = element?.getAttribute?.('role')

  if (/^h[1-6]$/.test(tagName)) return 'заголовок'
  if (/title|header/i.test(String(element?.className ?? ''))) return 'заголовок'
  if (tagName === 'button' || role === 'button') return 'кнопка'
  if (tagName === 'a') return element.closest('nav') ? 'раздел' : 'ссылка'
  if (['input', 'textarea', 'select'].includes(tagName)) return 'поле'
  if (tagName === 'img') return 'изображение'

  return 'элемент'
}

const getSpeakableElement = (target) => {
  if (!target?.closest) return null

  const semanticElement = target.closest(
    'button, a, input, textarea, select, img, h1, h2, h3, h4, h5, h6, [role="button"], [aria-label], [title]',
  )
  if (semanticElement) return semanticElement

  let element = target
  while (element && element !== document.body) {
    const text = getElementTextForSpeech(element)
    if (text && text.length <= 120) return element
    element = element.parentElement
  }

  return null
}

const getSpeechSynthesis = () => {
  if (typeof window === 'undefined') return null
  if (typeof window.speechSynthesis === 'undefined') return null
  if (typeof window.SpeechSynthesisUtterance === 'undefined') return null

  return window.speechSynthesis
}

const getRussianSpeechVoice = (synthesis) => {
  const voices = synthesis?.getVoices?.() ?? []

  return (
    voices.find((voice) => String(voice.lang).toLowerCase().startsWith('ru')) ||
    voices.find((voice) => /russian|рус/i.test(String(voice.name))) ||
    null
  )
}

const resumeSpeechSynthesis = (synthesis) => {
  if (!synthesis?.resume) return

  synthesis.resume()
}

const primeSpeechSynthesis = () => {
  const synthesis = getSpeechSynthesis()

  synthesis?.getVoices?.()
  resumeSpeechSynthesis(synthesis)
}

const speakText = (text) => {
  const synthesis = getSpeechSynthesis()
  if (!text || !synthesis) return

  const speakDelay = synthesis.getVoices?.().length ? 0 : 120

  synthesis.cancel()

  setTimeout(() => {
    const utterance = new SpeechSynthesisUtterance(text)
    const voice = getRussianSpeechVoice(synthesis)

    utterance.lang = 'ru-RU'
    utterance.rate = 1
    if (voice) utterance.voice = voice

    resumeSpeechSynthesis(synthesis)
    synthesis.speak(utterance)
    setTimeout(() => resumeSpeechSynthesis(synthesis), 0)
    setTimeout(() => resumeSpeechSynthesis(synthesis), 250)
  }, speakDelay)
}

const LowVisionSpeechToggle = ({
  enabled = false,
  activeValue,
  onChangeActive = () => {},
}) => {
  const [isActive, setIsActive] = useState(
    activeValue === undefined
      ? localStorage.getItem(lowVisionSpeechStorageKey) === '1'
      : isEnabledSetting(activeValue),
  )
  const lastSpokenRef = useRef('')
  const lastSpokenAtRef = useRef(0)

  useEffect(() => {
    if (activeValue !== undefined) {
      setIsActive(isEnabledSetting(activeValue))
    }
  }, [activeValue])

  useEffect(() => {
    if (!enabled && isActive) setIsActive(false)
  }, [enabled, isActive])

  useEffect(() => {
    localStorage.setItem(lowVisionSpeechStorageKey, isActive ? '1' : '0')
  }, [isActive])

  useEffect(() => {
    if (!enabled || !isActive) {
      window.speechSynthesis?.cancel?.()
      return undefined
    }

    primeSpeechSynthesis()

    const handleMouseOver = (event) => {
      const element = getSpeakableElement(event.target)
      if (!element) return

      const text = getElementTextForSpeech(element)
      if (!text) return

      const phrase = `${getElementSpeechPrefix(element)} ${text}`
      const now = Date.now()

      if (
        phrase === lastSpokenRef.current &&
        now - lastSpokenAtRef.current < 1500
      ) {
        return
      }

      lastSpokenRef.current = phrase
      lastSpokenAtRef.current = now
      speakText(phrase)
    }

    document.addEventListener('mouseover', handleMouseOver)

    return () => {
      document.removeEventListener('mouseover', handleMouseOver)
      window.speechSynthesis?.cancel?.()
    }
  }, [enabled, isActive])

  if (!enabled) return null

  return (
    <button
      type="button"
      className={classNames(s.lowVisionToggle, {
        [s.lowVisionToggleActive]: isActive,
      })}
      onClick={() => {
        const nextValue = !isActive
        setIsActive(nextValue)
        onChangeActive(nextValue)
      }}
      onPointerDown={primeSpeechSynthesis}
      aria-pressed={isActive}
      title="Режим для слабовидящих"
    >
      <span className={s.lowVisionToggleText}>Озвучка</span>
      <span className={s.lowVisionToggleSwitch} />
    </button>
  )
}

const BotActivationDropdown = ({
  showTelegram = false,
  showTelegramNotifications = false,
  telegramNotificationsStatus = 'hidden',
  showVkNotifications = false,
  vkNotificationsStatus = 'hidden',
  showMaxNotifications = false,
  maxNotificationsStatus = 'hidden',
  showMax = false,
  isMaxConnected = false,
  canConfigureNotificationIntegrations = false,
  onTelegramClick = () => {},
  onTelegramNotificationClick = () => {},
  onTelegramNotificationDetachClick = () => {},
  onTelegramNotificationSetupClick = () => {},
  onVkNotificationClick = () => {},
  onVkNotificationDetachClick = () => {},
  onVkNotificationSetupClick = () => {},
  onMaxNotificationClick = () => {},
  onMaxNotificationDetachClick = () => {},
  onMaxNotificationSetupClick = () => {},
  onMaxClick = () => {},
}) => {
  // Для не-админов пункты «Настроить» скрываем: в меню только то, что админ уже подключил
  const isTelegramNotificationsAvailable =
    showTelegramNotifications &&
    (telegramNotificationsStatus === 'activate' ||
      telegramNotificationsStatus === 'connected' ||
      (telegramNotificationsStatus === 'setup' &&
        canConfigureNotificationIntegrations))
  const isVkNotificationsAvailable =
    showVkNotifications &&
    (vkNotificationsStatus === 'activate' ||
      vkNotificationsStatus === 'connected' ||
      (vkNotificationsStatus === 'setup' && canConfigureNotificationIntegrations))
  const isMaxNotificationsAvailable =
    showMaxNotifications &&
    (maxNotificationsStatus === 'activate' ||
      maxNotificationsStatus === 'connected' ||
      (maxNotificationsStatus === 'setup' && canConfigureNotificationIntegrations))

  if (
    !showTelegram &&
    !isTelegramNotificationsAvailable &&
    !isVkNotificationsAvailable &&
    !isMaxNotificationsAvailable &&
    !showMax
  ) {
    return null
  }

  const items = [
    showTelegram && {
      key: 'telegram',
      label: 'Telegram',
      onClick: onTelegramClick,
    },
    isTelegramNotificationsAvailable &&
      telegramNotificationsStatus === 'setup' && {
        key: 'telegram-notifications-setup',
        label: 'Настроить Telegram-бот',
        onClick: onTelegramNotificationSetupClick,
      },
    isTelegramNotificationsAvailable &&
      telegramNotificationsStatus === 'activate' && {
        key: 'telegram-notifications',
        label: 'Привязать Telegram',
        onClick: onTelegramNotificationClick,
      },
    isTelegramNotificationsAvailable &&
      telegramNotificationsStatus === 'connected' && {
        key: 'telegram-notifications-detach',
        label: 'Отвязать Telegram',
        onClick: onTelegramNotificationDetachClick,
      },
    isVkNotificationsAvailable &&
      vkNotificationsStatus === 'setup' && {
        key: 'vk-notifications-setup',
        label: 'Настроить VK-сообщество',
        onClick: onVkNotificationSetupClick,
      },
    isVkNotificationsAvailable &&
      vkNotificationsStatus === 'activate' && {
        key: 'vk-notifications',
        label: 'Привязать VK',
        onClick: onVkNotificationClick,
      },
    isVkNotificationsAvailable &&
      vkNotificationsStatus === 'connected' && {
        key: 'vk-notifications-detach',
        label: 'Отвязать VK',
        onClick: onVkNotificationDetachClick,
      },
    isMaxNotificationsAvailable &&
      maxNotificationsStatus === 'setup' && {
        key: 'max-notifications-setup',
        label: 'Настроить MAX-бот',
        onClick: onMaxNotificationSetupClick,
      },
    isMaxNotificationsAvailable &&
      maxNotificationsStatus === 'activate' && {
        key: 'max-notifications',
        label: 'Привязать MAX',
        onClick: onMaxNotificationClick,
      },
    isMaxNotificationsAvailable &&
      maxNotificationsStatus === 'connected' && {
        key: 'max-notifications-detach',
        label: 'Отвязать MAX',
        onClick: onMaxNotificationDetachClick,
      },
    showMax && {
      key: 'max',
      label: isMaxConnected ? 'MAX привязан к текущему аккаунту' : 'MAX',
      disabled: isMaxConnected,
      onClick: onMaxClick,
    },
  ].filter(Boolean)

  const hasNotificationChannels =
    isTelegramNotificationsAvailable ||
    isVkNotificationsAvailable ||
    isMaxNotificationsAvailable

  let buttonText = 'Активировать ботов'
  if (hasNotificationChannels && !showTelegram && !showMax) {
    const channelParts = []
    if (isTelegramNotificationsAvailable) channelParts.push('Telegram')
    if (isVkNotificationsAvailable) channelParts.push('VK')
    if (isMaxNotificationsAvailable) channelParts.push('MAX')
    buttonText = `Подключить уведомления ${channelParts.join('/')}`
  } else if (hasNotificationChannels) {
    const parts = []
    const channelParts = []
    if (isTelegramNotificationsAvailable) channelParts.push('Telegram')
    if (isVkNotificationsAvailable) channelParts.push('VK')
    if (isMaxNotificationsAvailable) channelParts.push('MAX')
    parts.push(`уведомления ${channelParts.join('/')}`)
    if (showTelegram) parts.push('Telegram')
    if (showMax) parts.push('MAX')
    buttonText = `Подключить ${parts.join('/')}`
  } else if (showMax && showTelegram) {
    buttonText = 'Активировать MAX/Telegram бот'
  } else if (showMax) {
    buttonText = 'Активировать MAX-бота'
  } else if (showTelegram) {
    buttonText = 'Активировать Telegram бот'
  }

  return (
    <Dropdown
      menu={{ items }}
      trigger={['click']}
      getPopupContainer={(triggerNode) => triggerNode.parentElement}
    >
      <button
        className={classNames(s.secondaryButton, s.botActivationButton)}
        onClick={(event) => event.preventDefault()}
      >
        {buttonText}
      </button>
    </Dropdown>
  )
}

const HeaderPageSearch = ({ menu = [] }) => {
  const [value, setValue] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef(null)
  const history = useHistory()

  const pageItems = useMemo(
    () =>
      menu
        ?.flatMap((menuItem) =>
          (menuItem.items ?? []).map((item) => ({
            id: item.id,
            title: item.title,
            url: item.url,
            folderTitle: menuItem.folder?.title,
          })),
        )
        .filter((item) => item.title && item.url) ?? [],
    [menu],
  )

  const results = useMemo(() => {
    const search = normalizeSearchValue(value)
    const tokens = getSearchTokens(value)
    if (!search) return []

    const currentPageText = normalizeSearchValue(document.body?.innerText)
    const isCurrentPageMatch =
      currentPageText.includes(search) ||
      tokens.some((token) => currentPageText.includes(token))

    const currentPageResult = isCurrentPageMatch
      ? [
          {
            id: 'current-page',
            title: 'Найти на текущей странице',
            url: window.location.pathname,
            type: 'current-page',
          },
        ]
      : []

    const pageResults = pageItems
      .filter((item) => {
        const title = normalizeSearchValue(item.title)
        const url = normalizeSearchValue(item.url)
        const folderTitle = normalizeSearchValue(item.folderTitle)
        const aliases = normalizeSearchValue(getMenuItemSearchAliases(item))
        const searchable = `${title} ${url} ${folderTitle} ${aliases}`

        return (
          searchable.includes(search) ||
          tokens.some((token) => searchable.includes(token))
        )
      })
      .slice(0, 7)

    return [...currentPageResult, ...pageResults]
  }, [pageItems, value])

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'f') {
        event.preventDefault()
        inputRef.current?.focus()
        inputRef.current?.select()
        setIsOpen(true)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  useEffect(() => {
    setActiveIndex(0)
  }, [value])

  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setIsOpen(true)
      setActiveIndex((index) =>
        results.length ? Math.min(index + 1, results.length - 1) : 0,
      )
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((index) => Math.max(index - 1, 0))
    }

    if (event.key === 'Enter' && results[activeIndex]?.url) {
      event.preventDefault()
      if (results[activeIndex].type === 'current-page') {
        findOnCurrentPage(value)
        setIsOpen(false)
        return
      }

      const url = results[activeIndex].url

      if (url.startsWith('http')) window.open(url, '_blank', 'noreferrer')
      else history.push(url)

      setIsOpen(false)
    }

    if (event.key === 'Escape') {
      setValue('')
      setIsOpen(false)
    }
  }

  const isDropdownVisible = isOpen && value.trim()

  return (
    <div className={s.pageSearch}>
      <input
        ref={inputRef}
        className={s.pageSearchInput}
        value={value}
        onChange={(event) => {
          setValue(event.target.value)
          setIsOpen(true)
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setTimeout(() => setIsOpen(false), 120)}
        onKeyDown={onKeyDown}
        placeholder="Поиск по страницам"
      />
      {value && (
        <>
          <button
            type="button"
            className={s.pageSearchButton}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              setValue('')
              setIsOpen(false)
            }}
            title="Очистить"
          >
            ×
          </button>
          <button
            type="button"
            className={s.pageSearchButton}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => findOnCurrentPage(value, true)}
            title="Предыдущее совпадение"
          >
            ↑
          </button>
          <button
            type="button"
            className={s.pageSearchButton}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => findOnCurrentPage(value)}
            title="Следующее совпадение"
          >
            ↓
          </button>
        </>
      )}
      {isDropdownVisible && (
        <div className={s.pageSearchDropdown}>
          {results.length ? (
            results.map((item, index) => {
              const isCurrentPage = item.type === 'current-page'
              const isExternal = item.url.startsWith('http')
              const className = classNames(s.pageSearchResult, {
                [s.pageSearchResultActive]: index === activeIndex,
              })
              const content = (
                <>
                  <span className={s.pageSearchResultTitle}>{item.title}</span>
                  <span className={s.pageSearchResultPath}>
                    {isCurrentPage
                      ? 'Совпадение в открытой странице'
                      : `${item.folderTitle ? `${item.folderTitle} / ` : ''}${
                          item.url
                        }`}
                  </span>
                </>
              )

              if (isCurrentPage) {
                return (
                  <button
                    key={`${item.id}-${item.url}`}
                    type="button"
                    className={className}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      findOnCurrentPage(value)
                      setIsOpen(false)
                    }}
                  >
                    {content}
                  </button>
                )
              }

              if (isExternal) {
                return (
                  <a
                    key={`${item.id}-${item.url}`}
                    className={className}
                    href={item.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {content}
                  </a>
                )
              }

              return (
                <Link
                  key={`${item.id}-${item.url}`}
                  className={className}
                  to={item.url}
                  onClick={() => setIsOpen(false)}
                >
                  {content}
                </Link>
              )
            })
          ) : (
            <div className={s.pageSearchEmpty}>Ничего не найдено</div>
          )}
        </div>
      )}
    </div>
  )
}

const Header = () => {
  const dispatch = useDispatch()
  const { loadingAccounts, disconnectAccount, onLogin } = useAccountActions()
  const platformTime = useSelector((store) => store.platformTime)
  const storeSettings = useSelector((store) => store.settings)
  const menu = useSelector((store) => store.menu.items)

  const isMobile = useScreenWidth(992)

  const profile = useSelector(selectProfile)
  const isSeminarianLessonMode =
    profile?.role === 'seminarian' ||
    (['admin', 'junior-admin'].includes(profile?.role) &&
      profile?.roles?.includes('seminarian'))
  const handleLogout = async () => {
    if (!!loadingAccounts?.length) return
    if (
      !!localStorage.getItem(idUserAuthorizedOnTheAuthorizationPage) &&
      localStorage.getItem(idUserAuthorizedOnTheAuthorizationPage) !=
        profile?.id
    ) {
      await disconnectAccount(profile?.id)
      onLogin(localStorage.getItem(idUserAuthorizedOnTheAuthorizationPage))
    } else {
      AuthService.logout()
    }
  }

  const actualSettings = storeSettings?.status < 2 ? null : storeSettings

  // const showTelegramBotIntegrationButton = useTelegramBotIntegration(
  //   actualSettings,
  //   profile?.id,
  // )
  const showTelegramBotIntegrationButton = false

  const telegramNotificationBotIntegration = useTelegramNotificationBotIntegration(
    profile?.id,
    profile?.role,
  )
  const showTelegramNotificationBotIntegrationButton =
    telegramNotificationBotIntegration.status !== 'hidden'
  const telegramNotificationBotStatus =
    telegramNotificationBotIntegration.status
  const telegramNotificationBotIntegrationId =
    telegramNotificationBotIntegration.integrationId
  const telegramNotificationBotUsername =
    telegramNotificationBotIntegration.botUsername
  const telegramNotificationBotNecessarily =
    telegramNotificationBotIntegration.necessarily

  const vkNotificationCommunityIntegration = useVkNotificationCommunityIntegration(
    profile?.id,
    profile?.role,
  )
  const showVkNotificationCommunityIntegrationButton =
    vkNotificationCommunityIntegration.status !== 'hidden'
  const vkNotificationCommunityStatus =
    vkNotificationCommunityIntegration.status
  const vkNotificationCommunityIntegrationId =
    vkNotificationCommunityIntegration.integrationId
  const vkNotificationCommunityDisplayHandle =
    vkNotificationCommunityIntegration.displayHandle
  const vkNotificationCommunityNecessarily =
    vkNotificationCommunityIntegration.necessarily

  const maxNotificationBotIntegration = useMaxNotificationBotIntegration(
    profile?.id,
    profile?.role,
  )
  const showMaxNotificationBotIntegrationButton =
    maxNotificationBotIntegration.status !== 'hidden'
  const maxNotificationBotStatus = maxNotificationBotIntegration.status
  const maxNotificationBotIntegrationId =
    maxNotificationBotIntegration.integrationId
  const maxNotificationBotDisplayHandle =
    maxNotificationBotIntegration.displayHandle
  const maxNotificationBotNecessarily =
    maxNotificationBotIntegration.necessarily

  const openTelegramNotificationSettings = () => {
    window.location.assign(getNotificationIntegrationSettingsUrl('telegram'))
  }
  const openVkNotificationSettings = () => {
    window.location.assign(getNotificationIntegrationSettingsUrl('vkontakte'))
  }
  const openMaxNotificationSettings = () => {
    window.location.assign(getNotificationIntegrationSettingsUrl('max'))
  }

  const maxBotIntegrationStatus = useMaxBotIntegration(
    actualSettings,
    profile?.id,
    profile?.role,
  )
  const showMaxBotIntegrationButton = maxBotIntegrationStatus !== 'hidden'
  const isMaxBotConnected = maxBotIntegrationStatus === 'connected'

  const saveLowVisionSpeechMode = (value) => {
    const nextValue = value ? '1' : '0'
    const generalSettings = JSON.parse(
      localStorage.getItem(SETTINGS_TYPES.GENERAL) ?? '{}',
    )

    localStorage.setItem(lowVisionSpeechStorageKey, nextValue)
    localStorage.setItem(
      SETTINGS_TYPES.GENERAL,
      JSON.stringify({
        ...generalSettings,
        [lowVisionSpeechStorageKey]: nextValue,
      }),
    )
    dispatch(actionSetItemSetting(lowVisionSpeechStorageKey, nextValue))

    settingApi
      .update({
        key: lowVisionSpeechStorageKey,
        value: nextValue,
        type: SETTINGS_TYPES.GENERAL,
      })
      .catch((error) => {
        console.warn('Не удалось сохранить настройку озвучки:', error)
      })
  }

  const [reports, setReports] = useState([])
  const updateReports = async () => {
    try {
      const params = {
        filters: {
          ...(isSeminarianLessonMode && {
            seminarians: [profile.id],
            childrens: [],
            direction: null,
            groupsAndClasses: [],
            seminariansInCourses: [],
          }),
        },
        start: dayjs(platformTime?.platformTime).format('YYYY-MM-DD'),
        end: dayjs(platformTime?.platformTime).format('YYYY-MM-DD'),
        type: typesEnum.my,
      }

      const response = await timetableBeta.getWeeklyTimetableData(params)

      setReports(response.data?.data?.[0]?.reports ?? [])
    } catch (error) {
      setReports([])
    }
  }

  useEffect(() => {
    if (!profile?.role || platformTime?.status !== 'success') return
    updateReports()
    const intervalId = setInterval(updateReports, 283333)
    return () => clearInterval(intervalId)
  }, [profile?.role, profile?.id, isSeminarianLessonMode, platformTime?.status])

  const [logo, setLogo] = useState('')
  const [logoLoading, setLogoLoading] = useState(false)

  useEffect(() => {
    if (!actualSettings?.mainLogo) {
      setLogo('')
      setLogoLoading(false)
      return
    }

    if (actualSettings?.mainLogo) {
      setLogoLoading(true)
      images
        .getAccessCanvas({ imageUrl: actualSettings.mainLogo })
        .then((res) => {
          if (res?.data?.contents) setLogo(res.data.contents)
        })
        .finally(() => setLogoLoading(false))
    }
  }, [actualSettings?.mainLogo])

  if (isMobile)
    return (
      <Mobile
        {...{
          settings: actualSettings,
          profile,
          handleLogout,
          logoLoading,
          logo,
          showTelegramBotIntegrationButton,
          showTelegramNotificationBotIntegrationButton,
          telegramNotificationBotStatus,
          showVkNotificationCommunityIntegrationButton,
          vkNotificationCommunityStatus,
          showMaxNotificationBotIntegrationButton,
          maxNotificationBotStatus,
          showMaxBotIntegrationButton,
          isMaxBotConnected,
          telegramNotificationBotIntegrationId,
          telegramNotificationBotUsername,
          telegramNotificationBotNecessarily,
          vkNotificationCommunityIntegrationId,
          vkNotificationCommunityDisplayHandle,
          vkNotificationCommunityNecessarily,
          maxNotificationBotIntegrationId,
          maxNotificationBotDisplayHandle,
          maxNotificationBotNecessarily,
          openTelegramNotificationSettings,
          openVkNotificationSettings,
          openMaxNotificationSettings,
          isSeminarianLessonMode,
          reports,
          updateReports,
          menu,
          saveLowVisionSpeechMode,
        }}
      />
    )
  return (
    <Desktop
      {...{
        settings: actualSettings,
        profile,
        handleLogout,
        logoLoading,
        logo,
        showTelegramBotIntegrationButton,
        showTelegramNotificationBotIntegrationButton,
        telegramNotificationBotStatus,
        showVkNotificationCommunityIntegrationButton,
        vkNotificationCommunityStatus,
        showMaxNotificationBotIntegrationButton,
        maxNotificationBotStatus,
        showMaxBotIntegrationButton,
        isMaxBotConnected,
        telegramNotificationBotIntegrationId,
        telegramNotificationBotUsername,
        telegramNotificationBotNecessarily,
        vkNotificationCommunityIntegrationId,
        vkNotificationCommunityDisplayHandle,
        vkNotificationCommunityNecessarily,
        maxNotificationBotIntegrationId,
        maxNotificationBotDisplayHandle,
        maxNotificationBotNecessarily,
        openTelegramNotificationSettings,
        openVkNotificationSettings,
        openMaxNotificationSettings,
        isSeminarianLessonMode,
        reports,
        updateReports,
        menu,
        saveLowVisionSpeechMode,
      }}
    />
  )
}
export default Header

const Mobile = ({
  settings,
  profile,
  handleLogout,
  logo,
  logoLoading,
  showTelegramBotIntegrationButton,
  showTelegramNotificationBotIntegrationButton,
  telegramNotificationBotStatus,
  showVkNotificationCommunityIntegrationButton,
  vkNotificationCommunityStatus,
  showMaxNotificationBotIntegrationButton,
  maxNotificationBotStatus,
  showMaxBotIntegrationButton,
  isMaxBotConnected,
  telegramNotificationBotIntegrationId,
  telegramNotificationBotUsername,
  telegramNotificationBotNecessarily,
  vkNotificationCommunityIntegrationId,
  vkNotificationCommunityDisplayHandle,
  vkNotificationCommunityNecessarily,
  maxNotificationBotIntegrationId,
  maxNotificationBotDisplayHandle,
  maxNotificationBotNecessarily,
  openTelegramNotificationSettings,
  openVkNotificationSettings,
  openMaxNotificationSettings,
  isSeminarianLessonMode,
  reports,
  updateReports,
  menu,
  saveLowVisionSpeechMode,
}) => {
  const isTimetablePage = useIsCurrentPage('timetable')
  const dispatch = useDispatch()
  const { adminActive } = roleActive()
  const isCanSwitchBetweenPersonalAccounts =
    settings?.isCanSwitchBetweenPersonalAccounts == '1'
  const isDisableStudentSupportRequests =
    settings?.isDisableStudentSupportRequests == '1'
  const isDisableEmployeeSupportRequests =
    settings?.isDisableEmployeeSupportRequests == '1'
  const isDisableParentSupportRequests =
    settings?.isDisableParentSupportRequests == '1'

  const [open, setOpen] = useState(false)
  const [show, setShow] = useState(false)
  const [type, setType] = useState(false)

  const showDrawer = () => setOpen(true)
  const onClose = () => setOpen(false)
  const handleClick = () => {
    setShow(true)
    setType('ProblemSite')
    onClose()
  }
  const isMobile = useScreenWidth(540)

  const stickyRef = useRef(null)

  useEffect(() => {
    const element = stickyRef.current
    if (!element) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        entry.target.classList.toggle(s.stuck, !entry.isIntersecting)
      },
      {
        threshold: [1],
        rootMargin: '-1px 0px 0px 0px', // 1px отступ сверху для точного определения
      },
    )

    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const [showActivateTelegramBotModal, setShowActivateTelegramBotModal] =
    useState(false)
  const [
    showActivateTelegramNotificationBotModal,
    setShowActivateTelegramNotificationBotModal,
  ] = useState(false)
  const [
    showDeactivateTelegramNotificationBotModal,
    setShowDeactivateTelegramNotificationBotModal,
  ] = useState(false)
  const [
    showActivateVkNotificationCommunityModal,
    setShowActivateVkNotificationCommunityModal,
  ] = useState(false)
  const [
    showDeactivateVkNotificationCommunityModal,
    setShowDeactivateVkNotificationCommunityModal,
  ] = useState(false)
  const [
    showActivateMaxNotificationBotModal,
    setShowActivateMaxNotificationBotModal,
  ] = useState(false)
  const [
    showDeactivateMaxNotificationBotModal,
    setShowDeactivateMaxNotificationBotModal,
  ] = useState(false)
  const [showActivateMaxBotModal, setShowActivateMaxBotModal] = useState(false)
  const shouldShowLogo = settings === null || !!settings?.mainLogo

  const [isYandex, setIsYandex] = useState(false)

  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase()
    const isYandex = /yabrowser/i.test(userAgent)
    const isAndroid = /android/i.test(userAgent)

    if ((isYandex && isAndroid) || (isIOSDevice() && isYandex)) {
      setIsYandex(true)
    }
  }, [])

  const openModalListOfPersonalAccounts = () => {
    dispatch(openModal({ type: TYPES.LIST_OF_PERSONAL_ACCOUNTS }))
    onClose()
  }

  const hasAnyAdmin = ['admin', 'junior-admin'].some((item) =>
    profile?.roles?.includes(item),
  )

  return (
    <>
      <Drawer
        {...{
          placement: 'right',
          onClose: onClose,
          className: 'drawer-new-design',
          open: open,
          closable: false,
          width: isMobile ? '100vw' : 276,
          style: { height: '100vh' },
          children: (
            <div className={s.navList}>
              <div className={s.drawerHeader}>
                <div className={s.avatar}>
                  <img src={getAvatar(profile)} alt="Name" />
                </div>
                <div className={s.fio}>
                  {formateName(profile?.name, 'F I')}
                  {showUserCustomId(profile)}
                </div>
                <Link
                  to={['children', 'parent'].includes(profile?.role) ? '/profile' : '/personal'}
                  className={s.linkEdit}
                  onClick={onClose}
                >
                  <ButtonEditNew className={s.btnEdit} />
                </Link>
              </div>
              {isCanSwitchBetweenPersonalAccounts && (
                <button
                  style={{ marginBottom: '10px' }}
                  className={classNames(s.secondaryButton)}
                  onClick={openModalListOfPersonalAccounts}
                >
                  Сменить/добавить ЛК
                </button>
              )}

              {['admin', 'junior-admin'].includes(profile?.role) && (
                <LicenseAndPaymentButton className={s.support} />
              )}
              {((profile?.role === 'children' &&
                !isDisableStudentSupportRequests) ||
                (profile?.role === 'parent' &&
                  !isDisableParentSupportRequests) ||
                (profile?.role !== 'children' &&
                  profile?.role !== 'parent' &&
                  !isDisableEmployeeSupportRequests &&
                  !adminActive())) && (
                <div className={classNames(s.support)} onClick={handleClick}>
                  <SupportIcon />
                  Поддержка
                </div>
              )}

              <div
                onClick={handleLogout}
                className={classNames(
                  s.support,
                  s.exit,
                  'trackClickWhilePerformingWork',
                )}
              >
                <LogOutIcon />
                Выход
              </div>

              <NavigationApp onClose={onClose} />
              <BalanceBlock />
              <BalanceBlock type="additionalActivities" />
              <div className={s.close} onClick={onClose}>
                <CloseIcon />
              </div>
            </div>
          ),
        }}
      />

      <header
        style={{ display: isTimetablePage ? 'none' : undefined }}
        ref={stickyRef}
        className={classNames('app-header', s.wrapper)}
      >
        <div className={s.headerContainer}>
          {shouldShowLogo && (
            <div className={s.mainLogo}>
              <img
                src={logoLoading || settings === null ? '' : logo}
                alt="логотип"
                className={s.headerLogo}
              />
            </div>
          )}

          {isEnabledSetting(settings?.enableHeaderPageSearch) && (
            <HeaderPageSearch menu={menu} />
          )}

          <LowVisionSpeechToggle
            enabled={isEnabledSetting(settings?.enableLowVisionSupportMode)}
            activeValue={settings?.[lowVisionSpeechStorageKey]}
            onChangeActive={saveLowVisionSpeechMode}
          />

          <div className={s.mobIconsHeader}>
            <CustomGoogleTranslate />
            <ThemeSwitch />
            {/* <button className={s.buttonLanguage} title='Сменить язык'>
          <svg xmlns="http://www.w3.org/2000/svg" width="50" height="26" viewBox="0 0 50 26" fill="none">
          <rect x="0.5" y="0.5" width="49" height="25" rx="4.5" fill="white"/>
          <rect x="0.5" y="0.5" width="49" height="25" rx="4.5" stroke="var(--color-light-stroke)"/>
          <g clip-path="url(#clip0_124_4451)">
          <path d="M21.45 11.2028H5.55V8.03442C5.55 7.90969 5.65115 7.80854 5.77587 7.80854H21.2241C21.3489 7.80854 21.45 7.90969 21.45 8.03442L21.45 11.2028Z" fill="white" stroke="var(--color-light-stroke)" stroke-width="0.1"/>
          <path d="M21.2241 18.2413H5.77587C5.62353 18.2413 5.5 18.1178 5.5 17.9654V14.7471H21.5V17.9654C21.5 18.1178 21.3764 18.2413 21.2241 18.2413Z" fill="var(--color-flag-red)"/>
          <path d="M21.5 11.2528H5.5V14.7469H21.5V11.2528Z" fill="var(--color-flag-blue)"/>
          </g>
          <path d="M26.76 17V8.6H30.036C30.772 8.6 31.4 8.716 31.92 8.948C32.44 9.18 32.84 9.516 33.12 9.956C33.4 10.396 33.54 10.92 33.54 11.528C33.54 12.136 33.4 12.66 33.12 13.1C32.84 13.532 32.44 13.864 31.92 14.096C31.4 14.328 30.772 14.444 30.036 14.444H27.42L27.96 13.892V17H26.76ZM32.376 17L30.24 13.952H31.524L33.684 17H32.376ZM27.96 14.012L27.42 13.424H30C30.768 13.424 31.348 13.26 31.74 12.932C32.14 12.596 32.34 12.128 32.34 11.528C32.34 10.928 32.14 10.464 31.74 10.136C31.348 9.808 30.768 9.644 30 9.644H27.42L27.96 9.044V14.012ZM38.9588 17.096C37.8548 17.096 36.9868 16.78 36.3548 16.148C35.7228 15.516 35.4068 14.592 35.4068 13.376V8.6H36.6068V13.328C36.6068 14.264 36.8108 14.948 37.2188 15.38C37.6348 15.812 38.2188 16.028 38.9708 16.028C39.7308 16.028 40.3148 15.812 40.7228 15.38C41.1388 14.948 41.3468 14.264 41.3468 13.328V8.6H42.5108V13.376C42.5108 14.592 42.1948 15.516 41.5628 16.148C40.9388 16.78 40.0708 17.096 38.9588 17.096Z" fill="var(--color-text-main)"/>
          <defs>
          <clipPath id="clip0_124_4451">
          <rect width="16" height="16" fill="white" transform="translate(5.5 5)"/>
          </clipPath>
          </defs>
          </svg>
          </button> */}

            <RoleList userRole={profile.role} roles={profile.roles} />
            <Notification />
          </div>
          <button
            className={`burger burger-new ${s.burger}`}
            onClick={showDrawer}
          ></button>
        </div>

        <div className={s.headerContainer}>
          {(isSeminarianLessonMode || profile?.role === 'children') && (
            <div className={s.furthersWrapper}>
              <Further
                type="current"
                reports={reports}
                updateReports={updateReports}
                isSeminarianView={isSeminarianLessonMode}
              />
              <Further
                type="next"
                reports={reports}
                updateReports={updateReports}
                isSeminarianView={isSeminarianLessonMode}
              />
            </div>
          )}
        </div>

        {(showTelegramBotIntegrationButton ||
          showTelegramNotificationBotIntegrationButton ||
          showVkNotificationCommunityIntegrationButton ||
          showMaxNotificationBotIntegrationButton ||
          showMaxBotIntegrationButton) && (
          <div className={s.furthersWrapper} style={{ marginTop: '8px' }}>
            <div className={s.headerContainer}>
              <BotActivationDropdown
                showTelegram={showTelegramBotIntegrationButton}
                showTelegramNotifications={showTelegramNotificationBotIntegrationButton}
                telegramNotificationsStatus={telegramNotificationBotStatus}
                showVkNotifications={showVkNotificationCommunityIntegrationButton}
                vkNotificationsStatus={vkNotificationCommunityStatus}
                showMaxNotifications={showMaxNotificationBotIntegrationButton}
                maxNotificationsStatus={maxNotificationBotStatus}
                showMax={showMaxBotIntegrationButton}
                isMaxConnected={isMaxBotConnected}
                canConfigureNotificationIntegrations={hasAnyAdmin}
                onTelegramClick={() => setShowActivateTelegramBotModal(true)}
                onTelegramNotificationClick={() =>
                  setShowActivateTelegramNotificationBotModal(true)
                }
                onTelegramNotificationDetachClick={() =>
                  setShowDeactivateTelegramNotificationBotModal(true)
                }
                onTelegramNotificationSetupClick={openTelegramNotificationSettings}
                onVkNotificationClick={() =>
                  setShowActivateVkNotificationCommunityModal(true)
                }
                onVkNotificationDetachClick={() =>
                  setShowDeactivateVkNotificationCommunityModal(true)
                }
                onVkNotificationSetupClick={openVkNotificationSettings}
                onMaxNotificationClick={() =>
                  setShowActivateMaxNotificationBotModal(true)
                }
                onMaxNotificationDetachClick={() =>
                  setShowDeactivateMaxNotificationBotModal(true)
                }
                onMaxNotificationSetupClick={openMaxNotificationSettings}
                onMaxClick={() => setShowActivateMaxBotModal(true)}
              />
            </div>
          </div>
        )}
      </header>
      <QModals type={type} active={show} setActive={setShow} />
      <ActivateTelegramBot
        open={showActivateTelegramBotModal}
        setOpen={setShowActivateTelegramBotModal}
      />
      <ActivateTelegramNotificationBot
        open={showActivateTelegramNotificationBotModal}
        setOpen={setShowActivateTelegramNotificationBotModal}
        necessarily={telegramNotificationBotNecessarily}
        integrationId={telegramNotificationBotIntegrationId}
      />
      <ActivateMaxBot
        open={showActivateMaxBotModal}
        setOpen={setShowActivateMaxBotModal}
      />
      <DeactivateTelegramNotificationBot
        open={showDeactivateTelegramNotificationBotModal}
        setOpen={setShowDeactivateTelegramNotificationBotModal}
        integrationId={telegramNotificationBotIntegrationId}
        botUsername={telegramNotificationBotUsername}
      />
      <ActivateVkNotificationCommunity
        open={showActivateVkNotificationCommunityModal}
        setOpen={setShowActivateVkNotificationCommunityModal}
        necessarily={vkNotificationCommunityNecessarily}
        integrationId={vkNotificationCommunityIntegrationId}
      />
      <DeactivateVkNotificationCommunity
        open={showDeactivateVkNotificationCommunityModal}
        setOpen={setShowDeactivateVkNotificationCommunityModal}
        integrationId={vkNotificationCommunityIntegrationId}
        displayHandle={vkNotificationCommunityDisplayHandle}
      />
      <ActivateMaxNotificationBot
        open={showActivateMaxNotificationBotModal}
        setOpen={setShowActivateMaxNotificationBotModal}
        necessarily={maxNotificationBotNecessarily}
        integrationId={maxNotificationBotIntegrationId}
      />
      <DeactivateMaxNotificationBot
        open={showDeactivateMaxNotificationBotModal}
        setOpen={setShowDeactivateMaxNotificationBotModal}
        integrationId={maxNotificationBotIntegrationId}
        displayHandle={maxNotificationBotDisplayHandle}
      />
    </>
  )
}

const Desktop = ({
  settings,
  profile,
  handleLogout,
  logo,
  logoLoading,
  showTelegramBotIntegrationButton,
  showTelegramNotificationBotIntegrationButton,
  telegramNotificationBotStatus,
  showVkNotificationCommunityIntegrationButton,
  vkNotificationCommunityStatus,
  showMaxNotificationBotIntegrationButton,
  maxNotificationBotStatus,
  showMaxBotIntegrationButton,
  isMaxBotConnected,
  telegramNotificationBotIntegrationId,
  telegramNotificationBotUsername,
  telegramNotificationBotNecessarily,
  vkNotificationCommunityIntegrationId,
  vkNotificationCommunityDisplayHandle,
  vkNotificationCommunityNecessarily,
  maxNotificationBotIntegrationId,
  maxNotificationBotDisplayHandle,
  maxNotificationBotNecessarily,
  openTelegramNotificationSettings,
  openVkNotificationSettings,
  openMaxNotificationSettings,
  isSeminarianLessonMode,
  reports,
  updateReports,
  menu,
  saveLowVisionSpeechMode,
}) => {
  const isTimetablePage = useIsCurrentPage('timetable')
  const dispatch = useDispatch()

  const openAddVideoLesson = () => {
    dispatch(
      openModal({
        type: 'addVideoLesson',
        data: {
          type: 'add',
        },
      }),
    )
  }

  const { adminActive } = roleActive()
  const isCanSwitchBetweenPersonalAccounts =
    settings?.isCanSwitchBetweenPersonalAccounts == '1'
  const isDisableStudentSupportRequests =
    settings?.isDisableStudentSupportRequests == '1'
  const isDisableEmployeeSupportRequests =
    settings?.isDisableEmployeeSupportRequests == '1'
  const isDisableParentSupportRequests =
    settings?.isDisableParentSupportRequests == '1'
  const isShowChildRating = settings?.isShowRating === '1'
  const isShowEmployeeRating = settings?.isShowRatingEmployee === '1'
  const teacherLevelSettings =
    settings?.custom?.teacherLevelSettings ?? settings?.teacherLevelSettings
  const studentLevelSettings =
    settings?.custom?.studentLevelSettings ?? settings?.studentLevelSettings
  const isEmployeeRatingRole = [
    'seminarian',
    'senior-seminarian',
    'personal-manager',
    'hr-manager',
  ].includes(profile?.role)

  const [show, setShow] = useState(false)
  const [type, setType] = useState(false)
  const handleClick = () => {
    setShow(true)
    setType('ProblemSite')
  }
  const level = getMyLevel(
    profile?.pulses,
    parseLevels(
      isEmployeeRatingRole ? teacherLevelSettings : studentLevelSettings,
    ),
  )
  const gamificationBalance =
    profile?.gamification_balance ??
    profile?.gamificationBalance ??
    level.pulses

  const hasAnyAdmin = ['admin', 'junior-admin'].some((item) =>
    profile?.roles?.includes(item),
  )

  const isShowSupportButton = () => {
    if (['admin', 'junior-admin']?.includes(profile?.role)) {
      return true
    }
    if (
      settings?.isDisableStudentSupportRequests === '1' &&
      ['children']?.includes(profile?.role)
    ) {
      return false
    }
    if (
      settings?.isDisableEmployeeSupportRequests === '1' &&
      [
        'personal-manager',
        'seminarian',
        'senior-seminarian',
        'hr-manager',
      ]?.includes(profile?.role)
    ) {
      return false
    }
    if (
      settings?.isDisableParentSupportRequests === '1' &&
      ['parent']?.includes(profile?.role)
    ) {
      return false
    }
    return true
  }

  const [showActivateTelegramBotModal, setShowActivateTelegramBotModal] =
    useState(false)
  const [
    showActivateTelegramNotificationBotModal,
    setShowActivateTelegramNotificationBotModal,
  ] = useState(false)
  const [
    showDeactivateTelegramNotificationBotModal,
    setShowDeactivateTelegramNotificationBotModal,
  ] = useState(false)
  const [
    showActivateVkNotificationCommunityModal,
    setShowActivateVkNotificationCommunityModal,
  ] = useState(false)
  const [
    showDeactivateVkNotificationCommunityModal,
    setShowDeactivateVkNotificationCommunityModal,
  ] = useState(false)
  const [
    showActivateMaxNotificationBotModal,
    setShowActivateMaxNotificationBotModal,
  ] = useState(false)
  const [
    showDeactivateMaxNotificationBotModal,
    setShowDeactivateMaxNotificationBotModal,
  ] = useState(false)
  const [showActivateMaxBotModal, setShowActivateMaxBotModal] = useState(false)
  const shouldShowLogo = settings === null || !!settings?.mainLogo

  const openManualsModal = () => {
    dispatch(
      openModal({
        type: 'manualsModal',
        data: {},
      }),
    )
  }

  const openModalListOfPersonalAccounts = () => {
    dispatch(openModal({ type: TYPES.LIST_OF_PERSONAL_ACCOUNTS }))
  }

  return (
    <>
      <header
        style={{ display: isTimetablePage ? 'none' : undefined }}
        className={classNames('app-header', s.wrapper)}
      >
        <div className={s.headerWrapperLeft}>
          {shouldShowLogo && (
            <div className={s.logoDesktop}>
              <img
                src={logoLoading || settings === null ? '' : logo}
                alt="логотип"
              />
            </div>
          )}

          {isEnabledSetting(settings?.enableHeaderPageSearch) && (
            <HeaderPageSearch menu={menu} />
          )}

          <LowVisionSpeechToggle
            enabled={isEnabledSetting(settings?.enableLowVisionSupportMode)}
            activeValue={settings?.[lowVisionSpeechStorageKey]}
            onChangeActive={saveLowVisionSpeechMode}
          />

          {profile?.role === 'children' && isShowChildRating && (
            <div className={s.rating}>
              <div className={s.ratingIcon}>
                <RatingIcon style={{ width: 24, height: 24 }} />
                <svg
                  className={s.oldRatingIcon}
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12.9472 3.55557C13.2676 3.3908 13.6484 3.32107 14.0064 3.36095C14.4116 3.40609 14.7199 3.64354 14.9495 3.9038C15.1789 4.16382 15.3859 4.50947 15.586 4.90525C15.9831 5.69043 16.4311 6.83423 17.004 8.29688L17.019 8.33518C17.4744 9.49788 17.878 10.62 18.1687 11.5969C18.4542 12.5562 18.6496 13.4354 18.6496 14.087C18.6496 17.7205 15.6633 20.65 11.9996 20.65C8.3359 20.65 5.34961 17.7205 5.34961 14.087C5.34961 13.4782 5.51988 12.6745 5.7716 11.7984C6.02772 10.907 6.38517 9.88445 6.79404 8.81602C7.40279 7.22524 9.64961 7.74379 9.64961 9.35666C9.64961 9.96845 10.1555 10.4806 10.7996 10.4806C11.4437 10.4806 11.9496 9.96845 11.9496 9.35666V9.31582C11.9496 7.71975 11.9496 6.47293 12.0331 5.58575C12.0751 5.13887 12.1418 4.73758 12.2605 4.40975C12.379 4.0826 12.5804 3.74422 12.9472 3.55557ZM13.555 4.7051C13.5439 4.71886 13.5166 4.75912 13.4828 4.85235C13.4202 5.0254 13.3659 5.29772 13.3274 5.70753C13.2503 6.52617 13.2496 7.71136 13.2496 9.35666C13.2496 10.7043 12.1437 11.7806 10.7996 11.7806C9.4555 11.7806 8.34961 10.7043 8.34961 9.35666C8.34961 9.27681 8.32566 9.23883 8.308 9.21867C8.28549 9.19296 8.24877 9.17017 8.20199 9.16107C8.15522 9.15198 8.11584 9.15987 8.08991 9.17322C8.07089 9.18302 8.03663 9.20627 8.00817 9.28064C7.60628 10.3309 7.26299 11.3153 7.02105 12.1574C6.77471 13.0148 6.64961 13.6711 6.64961 14.087C6.64961 16.9847 9.0359 19.35 11.9996 19.35C14.9633 19.35 17.3496 16.9847 17.3496 14.087C17.3496 13.6377 17.2041 12.9135 16.9227 11.9677C16.6464 11.0394 16.2571 9.95465 15.8085 8.80929C15.2175 7.30044 14.7916 6.21497 14.4259 5.49194C14.243 5.1302 14.0949 4.90016 13.9746 4.76379C13.9063 4.68641 13.8653 4.65961 13.8506 4.65179C13.7536 4.64348 13.6421 4.66381 13.555 4.7051Z"
                    fill="var(--color-yellow)"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M5.99961 13.35C6.35859 13.35 6.64961 13.641 6.64961 14C6.64961 16.9547 9.04489 19.35 11.9996 19.35C14.9543 19.35 17.3496 16.9547 17.3496 14C17.3496 13.641 17.6406 13.35 17.9996 13.35C18.3586 13.35 18.6496 13.641 18.6496 14C18.6496 17.6727 15.6723 20.65 11.9996 20.65C8.32692 20.65 5.34961 17.6727 5.34961 14C5.34961 13.641 5.64062 13.35 5.99961 13.35Z"
                    fill="var(--color-text-main)"
                  />
                </svg>
              </div>
              <Tooltip title="Количество игровой валюты">
                <div className={s.ratingData}>{gamificationBalance}</div>
              </Tooltip>
            </div>
          )}
          {profile?.role === 'seminarian' && isShowEmployeeRating && (
            <div className={s.rating}>
              <div className={s.ratingIcon}>
                <RatingIcon style={{ width: 24, height: 24 }} />
                <svg
                  className={s.oldRatingIcon}
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12.9472 3.55557C13.2676 3.3908 13.6484 3.32107 14.0064 3.36095C14.4116 3.40609 14.7199 3.64354 14.9495 3.9038C15.1789 4.16382 15.3859 4.50947 15.586 4.90525C15.9831 5.69043 16.4311 6.83423 17.004 8.29688L17.019 8.33518C17.4744 9.49788 17.878 10.62 18.1687 11.5969C18.4542 12.5562 18.6496 13.4354 18.6496 14.087C18.6496 17.7205 15.6633 20.65 11.9996 20.65C8.3359 20.65 5.34961 17.7205 5.34961 14.087C5.34961 13.4782 5.51988 12.6745 5.7716 11.7984C6.02772 10.907 6.38517 9.88445 6.79404 8.81602C7.40279 7.22524 9.64961 7.74379 9.64961 9.35666C9.64961 9.96845 10.1555 10.4806 10.7996 10.4806C11.4437 10.4806 11.9496 9.96845 11.9496 9.35666V9.31582C11.9496 7.71975 11.9496 6.47293 12.0331 5.58575C12.0751 5.13887 12.1418 4.73758 12.2605 4.40975C12.379 4.0826 12.5804 3.74422 12.9472 3.55557ZM13.555 4.7051C13.5439 4.71886 13.5166 4.75912 13.4828 4.85235C13.4202 5.0254 13.3659 5.29772 13.3274 5.70753C13.2503 6.52617 13.2496 7.71136 13.2496 9.35666C13.2496 10.7043 12.1437 11.7806 10.7996 11.7806C9.4555 11.7806 8.34961 10.7043 8.34961 9.35666C8.34961 9.27681 8.32566 9.23883 8.308 9.21867C8.28549 9.19296 8.24877 9.17017 8.20199 9.16107C8.15522 9.15198 8.11584 9.15987 8.08991 9.17322C8.07089 9.18302 8.03663 9.20627 8.00817 9.28064C7.60628 10.3309 7.26299 11.3153 7.02105 12.1574C6.77471 13.0148 6.64961 13.6711 6.64961 14.087C6.64961 16.9847 9.0359 19.35 11.9996 19.35C14.9633 19.35 17.3496 16.9847 17.3496 14.087C17.3496 13.6377 17.2041 12.9135 16.9227 11.9677C16.6464 11.0394 16.2571 9.95465 15.8085 8.80929C15.2175 7.30044 14.7916 6.21497 14.4259 5.49194C14.243 5.1302 14.0949 4.90016 13.9746 4.76379C13.9063 4.68641 13.8653 4.65961 13.8506 4.65179C13.7536 4.64348 13.6421 4.66381 13.555 4.7051Z"
                    fill="var(--color-yellow)"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M5.99961 13.35C6.35859 13.35 6.64961 13.641 6.64961 14C6.64961 16.9547 9.04489 19.35 11.9996 19.35C14.9543 19.35 17.3496 16.9547 17.3496 14C17.3496 13.641 17.6406 13.35 17.9996 13.35C18.3586 13.35 18.6496 13.641 18.6496 14C18.6496 17.6727 15.6723 20.65 11.9996 20.65C8.32692 20.65 5.34961 17.6727 5.34961 14C5.34961 13.641 5.64062 13.35 5.99961 13.35Z"
                    fill="var(--color-text-main)"
                  />
                </svg>
              </div>
              <Tooltip title="Количество игровой валюты">
                <div className={s.ratingData}>{gamificationBalance}</div>
              </Tooltip>
            </div>
          )}

          {(isSeminarianLessonMode || profile?.role === 'children') && (
            <div className={s.furthersWrapper}>
              <Further
                type="current"
                reports={reports}
                updateReports={updateReports}
                isSeminarianView={isSeminarianLessonMode}
              />
              <Further
                type="next"
                reports={reports}
                updateReports={updateReports}
                isSeminarianView={isSeminarianLessonMode}
              />
            </div>
          )}
        </div>

        {/* {profile.role === 'seminarian' && <>
        <div className={classNames(s.buttonsWrapper, 'hideMobile')}>
          <button className={classNames(s.btnVideo)} onClick={() => openAddVideoLesson()}>
            <span className={s.loadVideoText}>Загрузить видео с семинара</span>
            <span className={s.icoWrap}>
              <GetIco className={s.ico} icon='video_camera' />
            </span>


          </button>
        </div>
      </>
      } */}

        <BotActivationDropdown
          showTelegram={showTelegramBotIntegrationButton}
          showTelegramNotifications={showTelegramNotificationBotIntegrationButton}
          telegramNotificationsStatus={telegramNotificationBotStatus}
          showVkNotifications={showVkNotificationCommunityIntegrationButton}
          vkNotificationsStatus={vkNotificationCommunityStatus}
          showMaxNotifications={showMaxNotificationBotIntegrationButton}
          maxNotificationsStatus={maxNotificationBotStatus}
          showMax={showMaxBotIntegrationButton}
          isMaxConnected={isMaxBotConnected}
          canConfigureNotificationIntegrations={hasAnyAdmin}
          onTelegramClick={() => setShowActivateTelegramBotModal(true)}
          onTelegramNotificationClick={() =>
            setShowActivateTelegramNotificationBotModal(true)
          }
          onTelegramNotificationDetachClick={() =>
            setShowDeactivateTelegramNotificationBotModal(true)
          }
          onTelegramNotificationSetupClick={openTelegramNotificationSettings}
          onVkNotificationClick={() =>
            setShowActivateVkNotificationCommunityModal(true)
          }
          onVkNotificationDetachClick={() =>
            setShowDeactivateVkNotificationCommunityModal(true)
          }
          onVkNotificationSetupClick={openVkNotificationSettings}
          onMaxNotificationClick={() =>
            setShowActivateMaxNotificationBotModal(true)
          }
          onMaxNotificationDetachClick={() =>
            setShowDeactivateMaxNotificationBotModal(true)
          }
          onMaxNotificationSetupClick={openMaxNotificationSettings}
          onMaxClick={() => setShowActivateMaxBotModal(true)}
        />

        {hasAnyAdmin && (
          <button className={s.secondaryButton} onClick={openManualsModal}>
            Курс по платформе
          </button>
        )}
        {isCanSwitchBetweenPersonalAccounts && (
          <button
            className={classNames(s.secondaryButton)}
            onClick={openModalListOfPersonalAccounts}
          >
            Сменить/добавить ЛК
          </button>
        )}
        <CustomGoogleTranslate />
        <ThemeSwitch />
        <RoleList userRole={profile.role} roles={profile.roles} />
        {/* {isShowSupportButton() && (
          <button
            className={s.button}
            title="Обратная связь"
            onClick={handleClick}
          >
            <Feedback />
          </button>
        )} */}

        <Notification />

        <button
          className={classNames(s.button, 'trackClickWhilePerformingWork')}
          title="Выйти"
          onClick={handleLogout}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M7 7.13193V6.61204C7 4.46614 7 3.3932 7.6896 2.79511C8.37919 2.19703 9.44136 2.34877 11.5657 2.65224L15.8485 3.26408C18.3047 3.61495 19.5327 3.79039 20.2664 4.63628C21 5.48217 21 6.72271 21 9.20377V14.7962C21 17.2773 21 18.5178 20.2664 19.3637C19.5327 20.2096 18.3047 20.385 15.8485 20.7359L11.5657 21.3478C9.44136 21.6512 8.37919 21.803 7.6896 21.2049C7 20.6068 7 19.5339 7 17.388V17.066"
              stroke="var(--color-text-main)"
              strokeWidth="1.5"
            />
            <path
              d="M16 12L16.5857 11.5315L16.9605 12L16.5857 12.4685L16 12ZM4 12.75C3.58579 12.75 3.25 12.4142 3.25 12C3.25 11.5858 3.58579 11.25 4 11.25V12.75ZM12.5857 6.53148L16.5857 11.5315L15.4143 12.4685L11.4143 7.46852L12.5857 6.53148ZM16.5857 12.4685L12.5857 17.4685L11.4143 16.5315L15.4143 11.5315L16.5857 12.4685ZM16 12.75H4V11.25H16V12.75Z"
              fill="var(--color-text-main)"
            />
          </svg>
        </button>
      </header>
      <QModals type={type} active={show} setActive={setShow} />
      <ActivateTelegramBot
        open={showActivateTelegramBotModal}
        setOpen={setShowActivateTelegramBotModal}
      />
      <ActivateTelegramNotificationBot
        open={showActivateTelegramNotificationBotModal}
        setOpen={setShowActivateTelegramNotificationBotModal}
        necessarily={telegramNotificationBotNecessarily}
        integrationId={telegramNotificationBotIntegrationId}
      />
      <DeactivateTelegramNotificationBot
        open={showDeactivateTelegramNotificationBotModal}
        setOpen={setShowDeactivateTelegramNotificationBotModal}
        integrationId={telegramNotificationBotIntegrationId}
        botUsername={telegramNotificationBotUsername}
      />
      <ActivateVkNotificationCommunity
        open={showActivateVkNotificationCommunityModal}
        setOpen={setShowActivateVkNotificationCommunityModal}
        necessarily={vkNotificationCommunityNecessarily}
        integrationId={vkNotificationCommunityIntegrationId}
      />
      <DeactivateVkNotificationCommunity
        open={showDeactivateVkNotificationCommunityModal}
        setOpen={setShowDeactivateVkNotificationCommunityModal}
        integrationId={vkNotificationCommunityIntegrationId}
        displayHandle={vkNotificationCommunityDisplayHandle}
      />
      <ActivateMaxNotificationBot
        open={showActivateMaxNotificationBotModal}
        setOpen={setShowActivateMaxNotificationBotModal}
        necessarily={maxNotificationBotNecessarily}
        integrationId={maxNotificationBotIntegrationId}
      />
      <DeactivateMaxNotificationBot
        open={showDeactivateMaxNotificationBotModal}
        setOpen={setShowDeactivateMaxNotificationBotModal}
        integrationId={maxNotificationBotIntegrationId}
        displayHandle={maxNotificationBotDisplayHandle}
      />
      <ActivateMaxBot
        open={showActivateMaxBotModal}
        setOpen={setShowActivateMaxBotModal}
      />
    </>
  )
}

function showUserCustomId(profile) {
  let customId = profile?.custom_id

  return customId && <span className={s.customId}>{customId}</span>
}

const parseLevels = (value) => {
  try {
    return value ? JSON.parse(value) : undefined
  } catch (error) {
    return undefined
  }
}
